import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
  Pagination,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  InputAdornment,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import { 
  Stars, 
  Close, 
  Search, 
  SentimentDissatisfied,
  ExpandMore,
  CalendarMonth
} from "@mui/icons-material";
import api from "../axios/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

const itemsPerPage = 15;

const Activity = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [totalClaimedPoints, setTotalClaimedPoints] = useState(0);
  const [activities, setActivities] = useState([]);
  const [historyActivities, setHistoryActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingReward, setClaimingReward] = useState(false);
  const [claimingActivityId, setClaimingActivityId] = useState(null);
  const [userName, setUserName] = useState("");

  //Verify User Modal
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // New state for filters
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [filterType, setFilterType] = useState({
    user: false,
    plant: false,
    status: false,
  });

  // Apply filters
  useEffect(() => {
    const currentActivities = activeTab === 0 ? activities : historyActivities;
    let filtered = [...currentActivities];

    if (searchQuery) {
      if (filterType.user) {
        filtered = filtered.filter((activity) =>
          activity.full_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      if (filterType.plant) {
        filtered = filtered.filter((activity) =>
          activity.treeName.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      if (filterType.status) {
        filtered = filtered.filter((activity) => {
          const status = getActivityStatus(activity);
          return status.toLowerCase().includes(searchQuery.toLowerCase());
        });
      }
    }

    if (selectedDate) {
      filtered = filtered.filter((activity) => {
        const activityDate = new Date(activity.created_at).setHours(0, 0, 0, 0);
        const filterDate = new Date(selectedDate).setHours(0, 0, 0, 0);
        return activityDate === filterDate;
      });
    }

    setFilteredActivities(filtered);
    setPage(1);
  }, [
    filterType,
    searchQuery,
    selectedDate,
    activities,
    historyActivities,
    activeTab,
  ]);

  const handleFilterTypeChange = (event) => {
    const { name, checked } = event.target;
    setFilterType((prev) => ({
      user: name === "user" ? checked : false,
      plant: name === "plant" ? checked : false,
      status: name === "status" ? checked : false,
    }));
    setSearchQuery(""); // Clear search when changing filter type
  };

  const getActivityStatus = (activity) => {
    if (activity.is_claimed === "0") return "Unclaimed";
    if (activity.is_claimed === "1" && activity.claim_passed === "0")
      return "Claimed";
    if (activity.is_claimed === "1" && activity.claim_passed === "1")
      return "Collected";
    if (activity.is_claimed === "1" && activity.claim_passed === "-1")
      return "Rejected";
    return "Unknown";
  };

  const getSearchPlaceholder = () => {
    if (filterType.user) return "Search by user name...";
    if (filterType.plant) return "Search by plant name...";
    if (filterType.status)
      return "Search by status (e.g., Claimed, Collected, Rejected)...";
    return "Select a filter type...";
  };

  //Send Verification Mail Function
  const handleSendVerificationEmail = async () => {
    setIsSendingEmail(true);
    try {
      const response = await api.post("/api/auth/send-verification-email");
      if (response.data.success) {
        toast.success(
          "Verification email sent! Please check your inbox and verify your account before trying again."
        );
        setIsVerificationModalOpen(false);
      } else {
        toast.error("Failed to send verification email. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to send verification email. Please try again.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [activeTab]);

  const displayValue = (value) => value || "-";

  const fetchActivities = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        const response = await api.get("/api/points/upload");
        setActivities(response.data.Points || []);
        setTotalClaimedPoints(response.data.totalPoints.collected_points);
        setUserName(response.data.totalPoints.full_name);
      } else {
        const response = await api.get("/api/points/history");
        setActivities(response.data.Points || []);
        setHistoryActivities(response.data.Points || []);
        setTotalClaimedPoints(response.data.totalPoints.collected_points);
        setUserName(response.data.totalPoints.full_name);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load activities");
      setActivities([]);
      setHistoryActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getPaginatedData = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredActivities.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  const paginatedActivities = getPaginatedData();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
  };

  const claimReward = async (id) => {
    setClaimingReward(true);
    setClaimingActivityId(id);
    try {
      const res = await api.post("/api/points/claim", { id });
      if (res.data.success) {
        toast.success("You've claimed your reward!");
      }
      if (res.data.Action === "Verify User") {
        setIsVerificationModalOpen(true);
      }
    } catch (error) {
      toast.error("Oops! Failed to claim the reward.");
    } finally {
      fetchActivities();
      setClaimingReward(false);
      setClaimingActivityId(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Custom EmptyState component
  const EmptyState = ({ message }) => (
    <Box 
      sx={{ 
        display: "flex", 
        flexDirection: "column",
        alignItems: "center", 
        justifyContent: "center",
        padding: 4,
        backgroundColor: "#f5f5f5",
        borderRadius: 2,
        margin: "16px 0"
      }}
    >
      <SentimentDissatisfied sx={{ fontSize: 60, color: "#9e9e9e", mb: 2 }} />
      <Typography variant="h6" color="text.secondary" align="center">
        {message}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
        Check back later or try different filters
      </Typography>
    </Box>
  );

  // Mobile Activity Card
  const ActivityCard = ({ activity }) => (
    <Card sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {activity.activity_code} ({activity.activity_points} pts)
        </Typography>
        
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            <span style={{color:"black"}}>User</span>: {activity.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span style={{color:"black"}}>Plant</span>: {activity.treeName}
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
  {format(new Date(activity.created_at), 'dd/MM/yyyy hh:mm a')}
</Typography>
        
        {activity.rejection_reason && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Rejection: {activity.rejection_reason}
          </Typography>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          {activity.is_claimed === "0" ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                claimingReward && claimingActivityId === activity.ap_id ? (
                  <CircularProgress size={16} />
                ) : (
                  <Stars style={{ color: "green" }} />
                )
              }
              onClick={() => claimReward(activity.ap_id)}
              disabled={claimingReward}
              sx={{
                backgroundColor: "rgba(255, 235, 59, 0.1)",
                color: "warning.dark",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "rgba(255, 235, 59, 0.2)",
                },
              }}
            >
              {claimingReward && claimingActivityId === activity.ap_id
                ? "Claiming..."
                : `${activity.activity_points} - Claim`}
            </Button>
          ) : activity.is_claimed === "1" && activity.claim_passed === "0" ? (
            <Typography color="text.primary">
              {activity.activity_points} Claimed
            </Typography>
          ) : activity.is_claimed === "1" && activity.claim_passed === "1" ? (
            <Typography color="text.primary">
              {activity.activity_points} Collected
            </Typography>
          ) : activity.is_claimed === "1" && activity.claim_passed === "-1" ? (
            <Typography style={{ color: "red" }}>
              0 - Claim Rejected
            </Typography>
          ) : (
            <></>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <Box sx={{ width: "100%", margin: "auto", padding: isMobile ? 1 : 2 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: isMobile ? 'center' : 'left' }}>
          Current Balance: {totalClaimedPoints} points ({userName})
        </Typography>
        
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          centered
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            marginBottom: 2,
            minHeight: '48px',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab
            label={isMobile ? "New Trees" : "New Tree Registered"}
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: isMobile ? 1 : 3,
              fontSize: isMobile ? '0.8rem' : 'inherit',
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />

          <Tab
            label={isMobile ? "Updates" : "Existing Tree Updates"}
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: isMobile ? 1 : 3,
              fontSize: isMobile ? '0.8rem' : 'inherit',
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
        </Tabs>

        {/* Mobile Filters Accordion */}
        {isMobile ? (
          <Accordion sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                backgroundColor: '#f5f5f5',
                '&.Mui-expanded': {
                  minHeight: '48px',
                }
              }}
            >
              <Typography>Filters</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>
              {/* Filter Type Checkboxes */}
              <FormGroup row sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterType.user}
                      onChange={handleFilterTypeChange}
                      name="user"
                      size="small"
                    />
                  }
                  label="User"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "black",
                      fontSize: '0.8rem',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterType.plant}
                      onChange={handleFilterTypeChange}
                      name="plant"
                      size="small"
                    />
                  }
                  label="Plant"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "black",
                      fontSize: '0.8rem',
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterType.status}
                      onChange={handleFilterTypeChange}
                      name="status"
                      size="small"
                    />
                  }
                  label="Status"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "black",
                      fontSize: '0.8rem',
                    },
                  }}
                />
              </FormGroup>

              <TextField
                label="Search"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={!filterType.user && !filterType.plant && !filterType.status}
                placeholder={getSearchPlaceholder()}
                fullWidth
                multiline={true}
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select Date"
                isClearable
                wrapperClassName="full-width-datepicker"
                style={{ width: "100%" }}
                customInput={
                  <TextField
                    label="Activity Date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline={true}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonth fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          /* Desktop Filters */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginBottom: 3,
            }}
          >
            {/* Filter Type Checkboxes */}
            <FormGroup row sx={{ gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterType.user}
                    onChange={handleFilterTypeChange}
                    name="user"
                  />
                }
                label="Filter by User"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "black",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterType.plant}
                    onChange={handleFilterTypeChange}
                    name="plant"
                  />
                }
                label="Filter by Plant"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "black",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterType.status}
                    onChange={handleFilterTypeChange}
                    name="status"
                  />
                }
                label="Filter by Status"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "black",
                  },
                }}
              />
            </FormGroup>

            {/* Search and Date Filters */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label="Search"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={!filterType.user && !filterType.plant && !filterType.status}
                placeholder={getSearchPlaceholder()}
                fullWidth
                multiline={true}
                rows={1}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />

              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select Date"
                isClearable
                customInput={
                  <TextField
                    label="Activity Date"
                    variant="outlined"
                    sx={{ minWidth: 200 }}
                    multiline={true}
                    rows={1}
                    size="small"
                  />
                }
              />
            </Box>
          </Box>
        )}

        {filteredActivities.length === 0 ? (
          <EmptyState
            message={
              activeTab === 0
                ? "No new tree registrations to display"
                : "No tree updates to display"
            }
          />
        ) : (
          <>
            {isMobile ? (
              /* Mobile Card View */
              <Box sx={{ mt: 2 }}>
                {paginatedActivities.map((activity) => (
                  <ActivityCard key={activity.id || activity.ap_id} activity={activity} />
                ))}
              </Box>
            ) : (
              /* Desktop Table View */
              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#002F43" }}>
                    <TableRow>
                      <TableCell sx={{ color: "#fff" }}>Activity</TableCell>
                      <TableCell sx={{ color: "#fff" }}>User</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Plant</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Activity Date/Time</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Collected Points</TableCell>
                      <TableCell sx={{ color: "#fff" }}>Rejection Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedActivities.map((row) => (
                      <TableRow key={row.id || row.ap_id}>
                        <TableCell>
                          <Typography>
                            {row.activity_code}({row.activity_points})
                          </Typography>
                        </TableCell>
                        <TableCell>{row.full_name}</TableCell>
                        <TableCell>{row.treeName}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                          {format(new Date(row.created_at), 'dd/MM/yyyy hh:mm a')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.is_claimed === "0" ? (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                  claimingReward && claimingActivityId === row.ap_id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <Stars style={{ color: "green" }} />
                                  )
                                }
                                onClick={() => claimReward(row.ap_id)}
                                disabled={claimingReward}
                                sx={{
                                  backgroundColor: "rgba(255, 235, 59, 0.1)",
                                  color: "warning.dark",
                                  fontWeight: "bold",
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 235, 59, 0.2)",
                                  },
                                }}
                              >
                                {claimingReward && claimingActivityId === row.ap_id
                                  ? "Claiming..."
                                  : `${row.activity_points} - Claim`}
                              </Button>
                            </>
                          ) : row.is_claimed === "1" && row.claim_passed === "0" ? (
                            <Typography color="text.primary">
                              {row.activity_points} Claimed
                            </Typography>
                          ) : row.is_claimed === "1" && row.claim_passed === "1" ? (
                            <Typography color="text.primary">
                              {row.activity_points} Collected
                            </Typography>
                          ) : row.is_claimed === "1" && row.claim_passed === "-1" ? (
                            <Typography style={{ color: "red" }}>
                              0 - Claim Rejected
                            </Typography>
                          ) : (
                            <></>
                          )}
                        </TableCell>
                        <TableCell
                          style={{ color: row.rejection_reason ? "red" : "inherit" }}
                        >
                          {displayValue(row.rejection_reason)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {totalPages > 1 && (
              <Box sx={{ 
                display: "flex", 
                justifyContent: "center", 
                marginTop: 2,
                '.MuiPaginationItem-root': {
                  ...(isMobile && { 
                    fontSize: '0.75rem',
                    minWidth: '28px',
                    height: '28px'
                  })
                }
              }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  className="pagination-container"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </>
        )}

        <ToastContainer 
          closeOnClick 
          position={isMobile ? "bottom-center" : "top-right"}
          autoClose={3000}
          hideProgressBar={isMobile}
          newestOnTop
          draggable
          pauseOnHover
        />
        
        <Dialog
          open={isVerificationModalOpen}
          onClose={() => setIsVerificationModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: isMobile ? '95%' : '500px',
              m: isMobile ? 1 : 'auto',
            }
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            Contact Verification Required
            <IconButton
              aria-label="close"
              onClick={() => setIsVerificationModalOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 2 }}>
              Your account needs to have at least one verified contact method (Email or Mobile Number) to claim rewards. Click <strong>Verify</strong> below to go to your profile page and complete verification.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                setIsVerificationModalOpen(false);
                navigate('/dashboard/profile');
              }}
              sx={{
                backgroundColor: "#4caf50",
                "&:hover": {
                  backgroundColor: "#388e3c",
                },
                fontWeight: "bold",
                px: 3,
              }}
            >
              Verify
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default Activity;